const resultsWorkspace = {
  analysis: {
    pending: 'Analysis pending',
    failure_mode: 'Generate failure modes',
    countermeasure: 'Generate countermeasures',
    analyze_one: 'Analyze {{count}} pending row',
    analyze_other: 'Analyze {{count}} pending rows',
    running: 'Analyzing…',
    guidance: 'Adopt and confirm pending rows first. Adopted or edited results are kept; new candidates require review.',
    saveTitle: 'Re-analyze after saving?',
    saveDescription: 'Re-analyze marks this row as analysis pending. Existing results stay until analysis succeeds. Keep current results saves your edits without requesting another analysis.',
    keep: 'Keep current results',
    reanalyze: 'Re-analyze',
    cancel: 'Cancel',
    completed: 'Analysis completed. Review the new candidates.',
    errors: {
      conflict: 'Analysis inputs changed. Load the latest content before analyzing pending rows again.',
      validation: 'The analysis request was refused. Check the pending rows and their review requirements, then load the latest content.',
      unexpected: 'The analysis result could not be confirmed. Load the latest content before trying again.',
      unavailable: {
        temporarilyUnavailable: 'Analysis is temporarily unavailable. Please retry.',
        serverError: 'The analysis result could not be confirmed because of a server error. Reload or retry to check the latest results.',
        network: 'A network error prevented confirmation of the analysis result. Reload or retry to check the latest results.',
        flagOff: 'Analysis is turned off in this environment. The results remain available to view.',
        flagUnconfirmed: 'Could not confirm whether analysis is available. Load the latest content before trying again.',
      },
    },
  },
  title: 'Analysis results',
  kinds: {
    design_change: 'Change points',
    failure_mode: 'Failure modes',
    countermeasure: 'Countermeasures',
  },
  view: {
    label: 'View',
    list: 'List',
    board: 'Board',
  },
  board: {
    show: {
      design_change: 'Change points: {{count}}, show on the board',
      failure_mode: 'Failure modes: {{count}}, show on the board',
      countermeasure: 'Countermeasures: {{count}}, show on the board',
    },
  },
  overview: {
    unlinked: 'Rows whose parent row is missing',
    noFailureModes: 'No failure modes in this view',
    noCountermeasures: 'No countermeasures in this view',
  },
  visibility: {
    label: 'Show',
    active: 'Active rows',
    withRejected: 'Include not adopted',
    all: 'Everything, including historical',
  },
  counts: {
    label: '{{kind}} counts',
    hiddenByAncestor: 'Under a parent not adopted or historical',
  },
  tags: {
    changePoint: 'Related change point {{code}}',
    failureMode: 'Failure mode {{code}}',
  },
  columns: {
    code: 'No.',
    status: 'Status',
    content: 'Content',
    originChange: 'Origin change point',
    originFailure: 'Origin failure mode',
    type: 'Type',
    severity: 'Severity',
  },
  openDetail: 'Open details of {{code}}',
  emptyKind: {
    design_change: 'No change points match this view',
    failure_mode: 'No failure modes match this view',
    countermeasure: 'No countermeasures match this view',
  },
  emptySession: 'This analysis has no results yet',
  load: {
    loading: 'Loading the analysis results',
    missing: 'This analysis was not found. It may have been deleted, or the URL may be wrong.',
    forbidden: 'You do not have permission to view this analysis.',
    signedOut: 'Your sign-in has expired. Please sign in again.',
    unavailable: 'The analysis results could not be loaded. Please try again shortly.',
    unexpected: 'The analysis results could not be shown.',
    retry: 'Reload',
    refreshFailed: 'The latest results could not be loaded. What is shown may be out of date.',
  },
  fields: {
    category: 'Category',
    item_name: 'Part or function',
    old_specification: 'Old specification',
    new_specification: 'New specification',
    change_description: 'Change',
    failure_description: 'Failure mode',
    causal_claims: 'Causal reasoning',
    failure_cause: 'Cause',
    failure_effect: 'Effect',
    severity_score: 'Severity',
    countermeasure_requirement: 'Countermeasure needed',
    countermeasure_not_required_reason: 'Why no countermeasure is needed',
    description: 'Countermeasure',
    countermeasure_type: 'Type',
    target_cause: 'Cause addressed',
    mechanism: 'How it works',
    verification_method: 'Verification method',
    acceptance_criteria: 'Acceptance criteria',
  },
  options: {
    countermeasure_requirement: { pending: 'Not decided', required: 'Needed', not_required: 'Not needed' },
    countermeasure_type: { prevention: 'Prevention', detection: 'Detection', mitigation: 'Mitigation' },
  },
  listSeparator: ', ',
  severityOption: '{{label}} ({{score}})',
  detail: {
    title: {
      design_change: 'Change point {{code}}',
      failure_mode: 'Failure mode {{code}}',
      countermeasure: 'Countermeasure {{code}}',
    },
    description: 'Check the content, edit and save it if needed, then adopt it or decide otherwise.',
    descriptionEditOnly: 'Check the content, and edit and save it if needed. This row cannot be decided yet.',
    descriptionDecideOnly: 'Check the content, then adopt it or decide otherwise. It cannot be edited here.',
    descriptionReadOnly: 'You can view this content. It cannot be edited or decided here.',
    savedNotShown: 'Recorded, but this screen could not show the update. Load the latest content.',
    reloaded: 'The latest content is loaded. Fields someone else changed are marked. Check them, then save again.',
    reloadedOther: 'The latest content is loaded. Someone else changed this item outside the fields shown here, such as its status or scores. Check it before you continue.',
    reloadedUnchanged: 'The latest content is loaded. Nothing in this item changed. Try again.',
    changedByOthers: 'Updated by someone else',
    claimsOriginUnrecorded: 'The origin of these claims is not recorded.',
    memory: 'Design decision memory',
    originChange: 'Origin change point',
    originFailure: 'Origin failure mode',
    plannedMeasures: 'Planned countermeasures',
    relatedFailures: 'Related failure modes',
    none: 'None',
    causalEmpty: 'There is no causal hypothesis assembled by AI',
    relatedKnowledge: 'Related knowledge summary',
    sourcesEmpty: 'No related documents',
    page: 'page {{page}}',
    openDocument: 'Open in Documents',
    openDocumentLabel: 'Open “{{title}}” in Documents',
    openExternal: 'Open source page',
    openExternalLabel: 'Open the source page of “{{title}}” (new tab)',
    preview: 'Preview',
    previewLabel: 'Preview “{{title}}”',
    previewLoading: 'Loading the document…',
    previewNotFound: 'Not found or no access',
    previewLoadFailed: "Couldn't load the document. Check the connection and try again.",
    previewRetry: 'Retry',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    decided: 'Decision recorded',
    close: 'Close',
    decisionLabel: 'Decision',
    saveFirst: 'Save your edits before making a decision.',
    reconfirm: 'I checked the change above this row and adopt it',
    decisions: { adopt: 'Adopt', reject: 'Not adopt', defer: 'On hold', needsChecking: 'Needs checking' },
    blocked: {
      parentNotAdopted: 'The row above this one is not adopted yet, so this row cannot be decided.',
      historical: 'Historical rows cannot be changed.',
      flagOff: 'Editing is not available in this environment.',
      integrityHold: 'This change cannot be adopted until the extracted content is checked.',
      missingRequiredContent: 'Fill in these fields to adopt: {{fields}}',
      kindNotAiEditable: 'AI cannot edit this row.',
      countermeasureNotRequired: 'This countermeasure belongs to a failure mode marked as needing none.',
    },
  },
  claims: {
    claim: 'Claim {{n}}',
    evidenceLevel: 'Evidence level for claim {{n}}',
    evidence: 'Evidence for claim {{n}}',
    level: { direct: 'Direct evidence', supported: 'Supporting evidence', assumption: 'Assumption' },
    add: 'Add claim',
    remove: 'Remove claim {{n}}',
    moveUp: 'Move claim {{n}} up',
    moveDown: 'Move claim {{n}} down',
    count: '{{n}} / {{max}}',
    empty: 'No claims yet.',
    limitReached: 'Up to {{max}} claims.',
    errors: {
      blank: 'Enter the claim.',
      level: 'Choose an evidence level.',
      tooLong: 'Use at most {{max}} characters.',
      tooMany: 'Up to {{max}} claims. Remove some to save.',
    },
  },
  discard: {
    title: 'You have unsaved changes',
    description: 'If you close now, the edits you have not saved are lost.',
    keep: 'Keep editing',
    discard: 'Discard and close',
  },
  create: {
    title: { design_change: 'Add change point', failure_mode: 'Add failure mode', countermeasure: 'Add countermeasure' },
    description: 'Enter the content, then add it.',
    parentPlaceholder: 'Choose one',
    noParent: 'A change point has no origin row.',
    noParentAvailable: {
      failure_mode: 'There is no origin change point to add under.', countermeasure: 'There is no origin failure mode to add under.',
    },
    add: 'Add',
    adding: 'Adding…',
    missing: 'This analysis was not found. It may have been deleted.',
    reloaded: 'The latest content is loaded. Check the content and the origin, then add again.',
    problems: {
      missingParent: { failure_mode: 'Choose the origin change point.', countermeasure: 'Choose the origin failure mode.' },
      missingItemName: { design_change: 'Enter the part or function.' },
      missingDescription: {
        design_change: 'Enter the change.', failure_mode: 'Enter the failure mode.', countermeasure: 'Enter the countermeasure.',
      },
      invalidClaims: 'Check the causal reasoning.',
    },
  },
  errors: {
    conflict: 'Someone else updated this content. Load the latest content, then save again. Your edits are kept.',
    conflictKept: 'Someone else updated this content. Your edits are kept.',
    reload: 'Load the latest content',
    reloadFailed: 'The latest content could not be loaded. Your edits are kept. Please try again.',
    stalePreview: 'The content changed after the AI proposal was made. Create a new proposal.',
    validation: 'The input could not be saved. Check the content. Your edits are kept.',
    missing: 'This row was not found. It may have been deleted.',
    unexpected: 'The change could not be saved. Your edits are kept.',
    retry: 'Retry',
    unavailable: {
      temporarilyUnavailable: 'Saving is temporarily unavailable. Your edits are kept. Please retry.',
      serverError: 'A server error stopped the save. Your edits are kept. Please retry.',
      network: 'A network error stopped the save. Your edits are kept. Please retry.',
      aiFailed: 'The AI step failed. Please retry.',
      flagOff: 'Editing is turned off in this environment. Your edits are kept.',
      flagUnconfirmed: 'The change could not be saved, and editing could not be confirmed as available.',
      forbidden: 'You do not have permission to do this.',
      signedOut: 'Your sign-in has expired. Please sign in again.',
    },
  },
  editing: {
    checking: 'Checking whether editing is available. Until then the results are read-only.',
    unavailable: 'Could not check whether editing is available. The results are read-only; reload the page to try again.',
  },
  readOnly: 'Editing analysis results is not enabled in this environment, so you can only view them.',
  status: {
    adopted: 'Adopted',
    notAdopted: 'Not adopted',
    onHold: 'On hold',
    needsChecking: 'Needs checking',
    unreviewed: 'Unreviewed',
    historical: 'Historical',
  },
  severity: {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    unset: 'Not set',
  },
  needsRecheck: 'Needs recheck',
  scoreReset: {
    warning: 'Saving this change returns the reviewed RPN to provisional. It will need review again.',
    acknowledge: 'I understand the RPN returns to provisional',
  },
  aiCausalHypothesis: 'Causal hypothesis assembled by AI',
  activeRows: 'Shown: {{count}}',
  exportAllRows: 'Excel export (all review states): {{count}} rows',
};
export default resultsWorkspace;
